import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../services/supabaseClient';
import { getUpstashSensorDataCache } from '../utils/upstashRedisCache';
import { AggregatedDataService } from '../services/AggregatedDataService';

/**
 * AppDataContext — a single global store for app data (sensors, gateways,
 * subscription) shared across ALL pages.
 *
 * Problem it solves: previously, every page mounted its own
 * useSensorsOptimized / useGatewaysOptimized / useSubscription hook, each of
 * which independently fetched the same data on mount. Navigating between
 * pages meant 3-5s spinners every time.
 *
 * Strategy: fetch-once-on-auth + stale-while-revalidate.
 *   - On login, fetch sensors + gateways + subscription ONCE.
 *   - All pages read from this shared context — instant navigation.
 *   - A SINGLE 60s background poll refreshes data silently (no spinners).
 *   - Pages can force-refresh after mutations (add device, upgrade, etc.).
 */

const AppDataContext = createContext(null);

// ─── Gateway helpers (extracted from useGatewaysOptimized) ───────────────
const toCleanMac = (mac) => (mac ? mac.replace(/[^0-9A-Fa-f]/g, '').toUpperCase() : '');

const getGatewayStatus = (minutesAgo) => {
  if (minutesAgo === null || minutesAgo === undefined) return 'offline';
  if (minutesAgo < 0) return 'online';
  if (minutesAgo <= 15) return 'online';
  if (minutesAgo <= 60) return 'warning';
  return 'offline';
};

// ─── Data fetchers (pure functions, no React) ───────────────────────────

async function fetchSensorsData(user) {
  if (!user) return [];
  const cache = getUpstashSensorDataCache();

  const { data: sensorsData, error } = await supabase
    .from('sensors')
    .select('*')
    .eq('user_id', user.id);
  if (error) throw error;
  if (!sensorsData || sensorsData.length === 0) return [];

  // Zones + gateways maps (parallel)
  const [{ data: zonesData }, { data: gatewaysData }] = await Promise.all([
    supabase.from('zones').select('id, name').eq('user_id', user.id),
    supabase.from('gateways').select('id, name, mac_address').eq('user_id', user.id).is('deleted_at', null),
  ]);

  const zonesMap = {};
  (zonesData || []).forEach((z) => { zonesMap[z.id] = z.name; });
  const gatewaysMap = {};
  (gatewaysData || []).forEach((g) => { gatewaysMap[g.id] = g.name || g.mac_address || 'Gateway'; });

  const sensorIds = sensorsData.map((s) => s.id.toString());

  // Batch fetch latest readings from Redis cache
  const cachedLatestReadings = await cache.batchGetLatestReadings(sensorIds);
  const missingReadingIds = sensorIds.filter((id) => !cachedLatestReadings[id]);

  // Fetch missing in parallel
  const latestReadingsPromises = missingReadingIds.map((id) =>
    getLatestReadingWithCache(id, cache, user)
  );
  const latestReadingsResults = await Promise.allSettled(latestReadingsPromises);

  const allLatestReadings = { ...cachedLatestReadings };
  const freshReadingsToCache = [];
  missingReadingIds.forEach((sensorId, index) => {
    const result = latestReadingsResults[index];
    if (result.status === 'fulfilled' && result.value) {
      allLatestReadings[sensorId] = result.value;
      freshReadingsToCache.push({ sensorId, reading: result.value });
    }
  });
  if (freshReadingsToCache.length > 0) {
    cache.batchCacheLatestReadings(freshReadingsToCache).catch(() => {});
  }

  return sensorsData.map((sensor) => {
    const latestReading = allLatestReadings[sensor.id.toString()];
    let assignmentName = 'Unassigned';
    if (sensor.zone_id && zonesMap[sensor.zone_id]) {
      assignmentName = zonesMap[sensor.zone_id];
    } else if (sensor.gateway_id && gatewaysMap[sensor.gateway_id]) {
      assignmentName = `Gateway: ${gatewaysMap[sensor.gateway_id]}`;
    }
    return {
      ...sensor,
      zone_name: assignmentName,
      latest_reading: latestReading || null,
      temperature_status: null,
      humidity_status: null,
      temperature_trend: null,
      humidity_trend: null,
      trend_data_points: 0,
      device_type: sensor.device_type || 'unknown',
      display_name: sensor.name,
      battery_percentage: null,
      battery_color: null,
    };
  });
}

async function getLatestReadingWithCache(sensorId, cache /*, user */) {
  try {
    const cachedLatest = await cache.getCachedLatestReading(sensorId);
    if (cachedLatest) return cachedLatest;

    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
    try {
      const hourlyData = await AggregatedDataService.fetchChartData(sensorId, 'HOUR', oneHourAgo, now);
      if (hourlyData?.readings?.length > 0) {
        const latest = hourlyData.readings[hourlyData.readings.length - 1];
        const formatted = {
          id: 0,
          sensor_id: sensorId,
          temperature: latest.temperature,
          humidity: latest.humidity,
          battery_level: latest.batteryLevel || 0,
          recorded_at: latest.timestamp.toISOString(),
          created_at: latest.timestamp.toISOString(),
        };
        await cache.cacheLatestReading(sensorId, formatted);
        return formatted;
      }
    } catch (e) {
      // fall through to historical cache
    }
    const cachedReadings = await cache.getCachedSensorReadings(sensorId, 'HOUR', oneHourAgo.toISOString(), now.toISOString());
    if (cachedReadings?.length > 0) {
      const latest = cachedReadings.sort((a, b) => new Date(b.recorded_at) - new Date(a.recorded_at))[0];
      await cache.cacheLatestReading(sensorId, latest);
      return latest;
    }
    const { data: latestReading } = await supabase
      .from('sensor_readings')
      .select('*')
      .eq('sensor_id', sensorId)
      .order('recorded_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (latestReading) {
      if (latestReading.recorded_at && !latestReading.recorded_at.includes('T')) {
        latestReading.recorded_at = new Date(latestReading.recorded_at).toISOString();
      }
      await cache.cacheLatestReading(sensorId, latestReading);
    }
    return latestReading;
  } catch {
    return null;
  }
}

async function fetchGatewaysData(user) {
  if (!user) return [];
  const cache = getUpstashSensorDataCache();

  const gatewayCacheKey = `gateway_list:${user.id}`;
  try {
    const cachedGateways = await cache.redisRequest(['GET', gatewayCacheKey]);
    if (cachedGateways) {
      const cachedData = JSON.parse(cachedGateways);
      const cacheAge = Date.now() - new Date(cachedData.cached_at).getTime();
      if (cacheAge < 120000) return cachedData.gateways;
    }
  } catch { /* fall through to DB */ }

  const { data: gatewaysData, error } = await supabase
    .from('gateways')
    .select('*')
    .eq('user_id', user.id)
    .is('deleted_at', null)
    .order('created_at', { ascending: false });
  if (error) throw error;
  if (!gatewaysData || gatewaysData.length === 0) return [];

  const gatewayIds = gatewaysData.map((g) => g.id);
  const { data: healthData } = await supabase
    .from('gateway_health_summary')
    .select('*')
    .in('gateway_id', gatewayIds);
  const healthMap = {};
  (healthData || []).forEach((h) => { healthMap[h.gateway_id] = h; });

  const enrichedGateways = await Promise.all(
    gatewaysData.map(async (gateway) => {
      const health = healthMap[gateway.id] || {};
      const { data: sensorsData } = await supabase
        .from('sensors')
        .select('id')
        .eq('user_id', user.id)
        .eq('gateway_id', gateway.id);
      const sensorIds = sensorsData?.map((s) => s.id) || [];
      const totalSensors = sensorIds.length;

      let onlineCount = 0, warningCount = 0, offlineCount = 0, gatewaySource = null;
      if (totalSensors > 0 && gateway.mac_address) {
        const cleanMac = toCleanMac(gateway.mac_address);
        if (cleanMac) {
          const { data: readingsData } = await supabase
            .from('gateway_sensor_readings')
            .select('sensor_id, timestamp, processed_at, source')
            .eq('gateway_id', gateway.id)
            .in('sensor_id', sensorIds)
            .order('timestamp', { ascending: false })
            .limit(1000);
          if (readingsData) {
            const latestBySensor = {};
            let latestOverallTimestamp = null;
            readingsData.forEach((reading) => {
              if (reading.sensor_id) {
                const ts = reading.timestamp || reading.processed_at;
                if (!latestBySensor[reading.sensor_id] || new Date(ts) > new Date(latestBySensor[reading.sensor_id])) {
                  latestBySensor[reading.sensor_id] = ts;
                }
              }
              const overallTs = reading.timestamp || reading.processed_at;
              if (!latestOverallTimestamp || new Date(overallTs) > new Date(latestOverallTimestamp)) {
                latestOverallTimestamp = overallTs;
                gatewaySource = reading.source;
              }
            });
            const missingSensorIds = sensorIds.filter((id) => !latestBySensor[id]);
            if (missingSensorIds.length > 0) {
              const { data: fallbackReadings } = await supabase
                .from('sensor_readings')
                .select('sensor_id, recorded_at, created_at, source')
                .in('sensor_id', missingSensorIds)
                .in('source', ['mqtt', 'http', 'app'])
                .order('recorded_at', { ascending: false })
                .limit(1000);
              if (fallbackReadings) {
                fallbackReadings.forEach((reading) => {
                  if (reading.sensor_id) {
                    const ts = reading.created_at || reading.recorded_at;
                    if (!latestBySensor[reading.sensor_id] || new Date(ts) > new Date(latestBySensor[reading.sensor_id])) {
                      latestBySensor[reading.sensor_id] = ts;
                    }
                  }
                });
              }
            }
            sensorIds.forEach((sensorId) => {
              const lastReading = latestBySensor[sensorId];
              if (lastReading) {
                const minutesAgo = Math.floor((Date.now() - new Date(lastReading).getTime()) / 60000);
                if (minutesAgo <= 15) onlineCount++;
                else if (minutesAgo <= 60) warningCount++;
                else offlineCount++;
              } else {
                offlineCount++;
              }
            });
          } else {
            offlineCount = totalSensors;
          }
        } else {
          offlineCount = totalSensors;
        }
      }

      // Gateway data status
      let dataStatus = { status: 'offline', minutesAgo: null, lastDataReceived: null };
      if (gateway.mac_address) {
        const cleanMac = toCleanMac(gateway.mac_address);
        if (cleanMac) {
          const statusCacheKey = `gateway_list_status:${cleanMac}`;
          try {
            const cachedStatus = await cache.redisRequest(['GET', statusCacheKey]);
            if (cachedStatus) {
              const statusData = JSON.parse(cachedStatus);
              if (Date.now() - new Date(statusData.cached_at).getTime() < 60000) {
                dataStatus = statusData;
              }
            }
          } catch { /* recompute below */ }
          if (!dataStatus.lastDataReceived) {
            let latestTs = gateway.last_seen_at ? new Date(gateway.last_seen_at) : null;
            try {
              const { data: latestUser } = await supabase
                .from('user_raw_gateway_data')
                .select('received_at')
                .eq('gateway_mac', cleanMac)
                .order('received_at', { ascending: false })
                .limit(1)
                .maybeSingle();
              if (latestUser?.received_at) {
                const t = new Date(latestUser.received_at);
                latestTs = !latestTs || t > latestTs ? t : latestTs;
              }
            } catch { /* ignore */ }
            if (latestTs) {
              const minutesAgo = Math.floor((Date.now() - latestTs.getTime()) / 60000);
              dataStatus = {
                status: getGatewayStatus(minutesAgo),
                minutesAgo,
                lastDataReceived: latestTs,
                isConnected: getGatewayStatus(minutesAgo) !== 'offline',
                cached_at: new Date().toISOString(),
              };
              try {
                await cache.redisRequest(['SETEX', statusCacheKey, '60', JSON.stringify(dataStatus)]);
              } catch { /* ignore */ }
            }
          }
        }
      }

      const lastSeenAt = dataStatus.lastDataReceived
        ? dataStatus.lastDataReceived.toISOString()
        : (health.last_seen_at || gateway.last_seen_at || gateway.last_seen);

      let finalSource = gatewaySource;
      if (!finalSource && gateway.mac_address) {
        try {
          const { data: latestReading } = await supabase
            .from('gateway_sensor_readings')
            .select('source')
            .eq('gateway_id', gateway.id)
            .order('timestamp', { ascending: false })
            .limit(1)
            .maybeSingle();
          if (latestReading?.source) finalSource = latestReading.source;
        } catch { /* ignore */ }
      }

      return {
        ...gateway,
        status: dataStatus.status || 'offline',
        last_seen_at: lastSeenAt,
        minutesAgo: dataStatus.minutesAgo,
        connectedDevices: totalSensors,
        onlineDevices: onlineCount,
        warningDevices: warningCount,
        offlineDevices: offlineCount,
        source: finalSource,
        health: {
          firmware_version: health.firmware_version,
          hardware_version: health.hardware_version,
          uptime_seconds: health.uptime_seconds,
          cpu_load: health.cpu_load,
          temperature: health.temperature,
          memory_free: health.memory_free,
          wan_ip: health.wan_ip,
          low_voltage: health.low_voltage,
        },
      };
    })
  );

  try {
    await cache.redisRequest(['SETEX', gatewayCacheKey, '120', JSON.stringify({
      gateways: enrichedGateways,
      cached_at: new Date().toISOString(),
    })]);
  } catch { /* ignore */ }

  return enrichedGateways;
}

async function fetchSubscriptionData(user, session, { sync = false } = {}) {
  if (!user || !session) return null;
  const { data: { session: freshSession } } = await supabase.auth.getSession();
  const token = freshSession?.access_token || session?.access_token;
  if (!token) return null;

  const syncFlag = sync ? '?sync=1' : '';
  const response = await fetch(`/.netlify/functions/get-subscription${syncFlag}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const contentType = response.headers.get('Content-Type') || '';
  if (!response.ok || !contentType.includes('application/json')) return null;
  const data = await response.json();
  return data.subscription;
}

// ─── Provider ───────────────────────────────────────────────────────────

export const AppDataProvider = ({ children }) => {
  const { user, session, isAuthenticated } = useAuth();

  const [sensors, setSensors] = useState([]);
  const [gateways, setGateways] = useState([]);
  const [subscription, setSubscription] = useState(null);

  const [sensorsLoading, setSensorsLoading] = useState(true);
  const [gatewaysLoading, setGatewaysLoading] = useState(true);
  const [subLoading, setSubLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const [sensorsError, setSensorsError] = useState(null);
  const [gatewaysError, setGatewaysError] = useState(null);

  // Refs to avoid stale closures in the poll interval
  const userRef = useRef(user);
  const sessionRef = useRef(session);
  const isAuthenticatedRef = useRef(isAuthenticated);
  const hasInitialLoad = useRef(false);
  const pollTimerRef = useRef(null);
  const isFetchingRef = useRef(false);

  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { sessionRef.current = session; }, [session]);
  useEffect(() => { isAuthenticatedRef.current = isAuthenticated; }, [isAuthenticated]);

  // ---- The single background refresh (stale-while-revalidate) ----
  const backgroundRefresh = useCallback(async () => {
    const u = userRef.current;
    const s = sessionRef.current;
    if (!isAuthenticatedRef.current || !u || isFetchingRef.current) return;
    isFetchingRef.current = true;
    setSyncing(true);
    try {
      // Fetch all three in parallel — no waterfall.
      const [sensorsResult, gatewaysResult, subResult] = await Promise.allSettled([
        fetchSensorsData(u),
        fetchGatewaysData(u),
        fetchSubscriptionData(u, s),
      ]);
      if (sensorsResult.status === 'fulfilled') setSensors(sensorsResult.value);
      else setSensorsError(sensorsResult.reason?.message || 'Failed to fetch sensors');
      if (gatewaysResult.status === 'fulfilled') setGateways(gatewaysResult.value);
      else setGatewaysError(gatewaysResult.reason?.message || 'Failed to fetch gateways');
      if (subResult.status === 'fulfilled') setSubscription(subResult.value);
    } finally {
      isFetchingRef.current = false;
      setSyncing(false);
    }
  }, []);

  // ---- Initial load on auth ----
  useEffect(() => {
    if (!isAuthenticated || !user || !session) {
      setSensors([]);
      setGateways([]);
      setSubscription(null);
      setSensorsLoading(true);
      setGatewaysLoading(true);
      setSubLoading(true);
      hasInitialLoad.current = false;
      return;
    }

    let cancelled = false;
    (async () => {
      isFetchingRef.current = true;
      // First subscription fetch reconciles with Stripe; sensors + gateways
      // are DB/Redis only so they're fast.
      const [sensorsResult, gatewaysResult, subResult] = await Promise.allSettled([
        fetchSensorsData(user),
        fetchGatewaysData(user),
        fetchSubscriptionData(user, session, { sync: true }),
      ]);
      if (cancelled) return;
      if (sensorsResult.status === 'fulfilled') setSensors(sensorsResult.value);
      else setSensorsError(sensorsResult.reason?.message || 'Failed to fetch sensors');
      if (gatewaysResult.status === 'fulfilled') setGateways(gatewaysResult.value);
      else setGatewaysError(gatewaysResult.reason?.message || 'Failed to fetch gateways');
      if (subResult.status === 'fulfilled') setSubscription(subResult.value);

      setSensorsLoading(false);
      setGatewaysLoading(false);
      setSubLoading(false);
      hasInitialLoad.current = true;
      isFetchingRef.current = false;
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id]);

  // ---- Single background poll (60s) — replaces per-page intervals ----
  useEffect(() => {
    if (!isAuthenticated) {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return;
    }
    // Start polling only after the initial load completes.
    pollTimerRef.current = setInterval(() => {
      if (hasInitialLoad.current && !isFetchingRef.current) {
        backgroundRefresh();
      }
    }, 60000);

    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    };
  }, [isAuthenticated, backgroundRefresh]);

  // ---- Manual refresh methods (used after mutations) ----
  const refreshSensors = useCallback(async () => {
    const u = userRef.current;
    if (!u) return;
    try {
      setSensorsError(null);
      const result = await fetchSensorsData(u);
      setSensors(result);
    } catch (e) {
      setSensorsError(e.message || 'Failed to fetch sensors');
    }
  }, []);

  const refreshGateways = useCallback(async () => {
    const u = userRef.current;
    if (!u) return;
    try {
      setGatewaysError(null);
      const cache = getUpstashSensorDataCache();
      const gatewayCacheKey = `gateway_list:${u.id}`;
      try { await cache.redisRequest(['DEL', gatewayCacheKey]); } catch { /* ignore */ }
      const result = await fetchGatewaysData(u);
      setGateways(result);
    } catch (e) {
      setGatewaysError(e.message || 'Failed to fetch gateways');
    }
  }, []);

  const refreshSubscription = useCallback(async ({ sync = false } = {}) => {
    const u = userRef.current;
    const s = sessionRef.current;
    if (!u || !s) return;
    const result = await fetchSubscriptionData(u, s, { sync });
    setSubscription(result);
  }, []);

  const refreshAll = useCallback(() => {
    backgroundRefresh();
  }, [backgroundRefresh]);

  const hasActiveSubscription = !!subscription && subscription.status === 'active';

  const value = {
    // Data
    sensors,
    gateways,
    gatewaysCount: gateways.length,
    subscription,
    hasActiveSubscription,

    // Loading flags
    loading: sensorsLoading || gatewaysLoading || subLoading,
    sensorsLoading,
    gatewaysLoading,
    subLoading,
    syncing,

    // Errors
    sensorsError,
    gatewaysError,

    // Refresh actions
    refreshSensors,
    refreshGateways,
    refreshSubscription,
    refreshAll,

    // Convenience helpers (match the old useSubscription API)
    canAddSensors: hasActiveSubscription,
    canAddGateways: hasActiveSubscription,
    canAddZones: hasActiveSubscription,
    canUseAIAnalysis: hasActiveSubscription,
    canAccessDashboard: hasActiveSubscription,
    canUseVerifyReports: hasActiveSubscription,
    subscriptionRequired: !subLoading && !hasActiveSubscription && isAuthenticated,
  };

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
};

export const useAppData = () => {
  const ctx = useContext(AppDataContext);
  if (!ctx) {
    throw new Error('useAppData must be used within an AppDataProvider');
  }
  return ctx;
};

export default AppDataContext;
